import { FieldViewModel, DatalayersMap, DatalayerZone, defineDirective, IDataLayer, IDataLayerType, IOmniaShell, LayerViewModel, setupVariants, showLayers } from "../../omnia"
import { IDatalayerMapDependencies } from "../../types"
import { getClassesByCount, getClassesByStep, getLimits, getSourceZones, isDiscreteType, round, setDefaultDiscreteTargetValues, setDefaultTargetValue, swapTargetValues, syncClasses } from "./classes"
import { createTargetLayer, simplify } from "./simplify"
import { mergeSmallZones } from "./small"
import { Class, PopupArgs, SourceZone } from "./types"

export class SimplifyPopup extends omnia.shell.Dialog {

    static create(): ng.IDirective {

        return defineDirective(SimplifyPopup, "app/datalayers/edit/simplify/popup.html", {})

    }

    private _block: boolean

    public syncClasses = (i: number) => syncClasses(this.classes, i)

    public sourceLayer: IDataLayer
    private field: FieldViewModel
    private dynamicScale: boolean

    public targetType: IDataLayerType
    public rounding = 0.1

    public classInputStep = 0.05

    public limits: { min: number, max: number }
    public sourceZones: SourceZone[]

    public sourceMap: DatalayersMap
    public targetMap: DatalayersMap

    public targetLayer: LayerViewModel
    public targetZones: { id: number, zone: DatalayerZone, cls: Class }[]
    private _targetClasses: Class[]
    private _targetMinArea: number

    private _fields: FieldViewModel[]

    public classes: Class[]
    private _step: number
    private _count: number

    public mergeSmallZones = false
    public minimumArea = 100

    static $inject = ["$scope", "OmniaShell", "OmniaDatalayersDependencies", "OmniaMapTopologyService"]

    constructor($scope: ng.IScope, shell: IOmniaShell, private mapCtxt: IDatalayerMapDependencies, private topo: omnia.map.IMapTopologyService) {
        super($scope, shell)
    }

    $onInit() {

        const args = this.arguments as PopupArgs

        this.sourceLayer = args.sourceLayer
        this.field = args.field
        this.dynamicScale = args.dynamicScale
        this.targetType = args.targetType || args.sourceLayer.Type


        const rnd = this.sourceLayer.Type.DefaultRounding
        this.rounding = rnd ? round(1 / rnd, 1) : 10

        this.setupField()

        this.limits = getLimits(this.sourceLayer.Points)
        this.sourceZones = getSourceZones(this.sourceLayer, z => this.topo.getArea(z.Geometry))
        this.classCount = (isDiscreteType(this.targetType) ? this.targetType.Values.length : 0) || 5

        this.classInputStep = this.sourceLayer.Type?.DefaultRounding || 0.1

        this.setupSourceMap()

    }

    private setupField() {

        const fieldSvc = this.mapCtxt.fieldSvc
        const field = fieldSvc.findField(this.sourceLayer.FieldId)
        const croppingId = this.sourceLayer.CroppingId
        const cropping = croppingId ? field.croppings.filter(c => c.croppingId == croppingId) : field.croppings

        this._fields = [field].concat(cropping).filter(i => !!i)

    }

    private setupSourceMap() {
        this.sourceMap = this.showLayer(this.sourceMap, this.sourceLayer, this.dynamicScale).map
    }

    private setupTargetMap(layer: IDataLayer) {

        const shouldInit = !this.targetMap

        const { map, layer: lvm } = this.showLayer(this.targetMap, layer)

        this.targetMap = map

        if (shouldInit) {
            this.targetMap.map.getMap().setView(this.sourceMap.map.getView())
        }

        return lvm

    }

    private showLayer(map: DatalayersMap, layer: IDataLayer, dynamicScale = false) {

        if (map) {
            map.resetDefaultLayers()
        }
        else {
            map = new DatalayersMap(this.mapCtxt, { viewDefaultExtent: false })
        }

        const ctxt = this.mapCtxt.createContext(() => [])
        const lvm = new LayerViewModel(ctxt, map, layer, this.field)

        if (dynamicScale && lvm.datalayer.Parent) {
            setupVariants([lvm], lvm.type)
        }

        showLayers(ctxt, [lvm], () => map.viewExtent(lvm.getExtent()))

        map.fieldsLayer.highlight(this._fields)
        map.croppingLayer.highlight(this._fields)

        map.setSelectableLayers([])

        return { map, layer: lvm }

    }

    get running() {
        return this._block
    }

    get step() {
        return this._step
    }

    set step(step) {
        this._step = step
        this._count = undefined
        const getClasses = getClassesByStep(this.limits)
        this.setupClasses(step, getClasses)
    }

    get classCount() {
        return this._count
    }

    set classCount(count) {
        this._count = count
        this._step = undefined
        const getClasses = getClassesByCount(this.limits)
        this.setupClasses(count, getClasses)
    }

    get targetZonesCount() {
        return this.targetLayer ? this.targetLayer.datalayer.Zones.length : undefined
    }

    get targetNumericType() {
        return this.targetType && this.targetType.ValueType == omnia.models.LayerValueType.Numeric
    }

    get targetSupportedValues() {
        return this.targetType ? this.targetType.Values : undefined
    }

    swapTargetValues() {
        swapTargetValues(this.classes)
        this.refreshTargetValue()
    }

    private setupClasses(value: number, setter: (zones: SourceZone[], value: number, rounding: number) => Class[]) {

        const classes = setter(this.sourceZones, value, this.rounding)

        this.classes = (this.targetType.TypeId == this.sourceLayer.TypeId)
            ? setDefaultTargetValue(classes, this.rounding)
            : isDiscreteType(this.targetType)
                ? setDefaultDiscreteTargetValues(classes, this.targetType)
                : classes

    }

    canCommit() {
        return (!this._block) && (!!this.targetLayer) && (!this.haveSettingsChanged())
    }

    commit() {

        if (this.canCommit()) {
            this.targetLayer = prepareCommit(this.targetLayer)
            this.close(this.targetLayer)
        }

    }

    canShowPreview() {
        return (!this._block) && this.classes && (this.classes.length > 1) && this.haveSettingsChanged() && ((!this.mergeSmallZones) || (this.minimumArea > 0))
    }

    showPreview() {

        if (!this.canShowPreview()) return

        this._block = true
        this.targetLayer = null
        this.targetZones = null

        this.shell.timeout(100, true)
            .then(() => {

                let targetZones = simplify(this.sourceZones, this.classes, this.topo)

                const minArea = this.mergeSmallZones ? this.minimumArea : undefined

                if (minArea) {
                    targetZones = mergeSmallZones(this.topo, targetZones, minArea)
                }

                const src = this.sourceLayer

                const targetLayerTmpl = {
                    FieldId: src.FieldId,
                    CroppingId: src.CroppingId,
                    Crop: src.Crop,
                    CropId: src.CropId,
                    FirstYearId: src.FirstYearId
                }

                const { layer, zones } = createTargetLayer(targetLayerTmpl, targetZones, this.topo, this.targetType)

                const lvm = this.setupTargetMap(layer)

                this.targetLayer = lvm

                this.targetZones = zones.map(i => ({
                    ...i,
                    zone: lvm.mapLayer.zones.find(z => z.id == i.id)
                }))

                this._targetClasses = this.classes.map(c => ({ ...c }))
                this._targetMinArea = minArea


            })
            .finally(() => {

                this._block = false

            })

    }

    refreshTargetValue(cls?: Class) {

        if (!this.targetZones) return

        const zones = cls
            ? this.targetZones.filter(tz => tz.cls == cls)
            : this.targetZones

        zones.forEach(z => z.zone.setValue(z.cls.targetValue))

        this.targetLayer.refresh()

    }

    haveSettingsChanged() {

        const a = this.classes || []
        const b = this._targetClasses || []

        if ((a.length != b.length) || a.some((x, index) => (b[index].min != x.min) || (b[index].max != x.max))) {
            return true
        }

        const minArea = this.mergeSmallZones ? this.minimumArea : undefined

        if (minArea != this._targetMinArea) {
            return true
        }

        return false

    }

}

function prepareCommit(layer: LayerViewModel) {

    layer.mapLayer.zones.forEach(z => {
        z.zone.Value = z.value
    })

    return layer

}
